import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import FlagIcon from "@mui/icons-material/Flag";
import EpicFlagsDialog from "../dialogs/EpicFlagsDialog";

const ROW_COLORS = {
  ARRIVED_PAID: { bg: "#ccfbf1", color: "#0f766e", hoverBg: "#99f6e4" },
  ARRIVED: { bg: "#d1fae5", color: "#065f46", hoverBg: "#a7f3d0" },
  PAID: { bg: "#ede9fe", color: "#5b21b6", hoverBg: "#ddd6fe" },
  ON_HOLD: { bg: "#fef3c7", color: "#b45309", hoverBg: "#fde68a" },
  OTHER: { bg: "#f1f5f9", color: "#64748b", hoverBg: "#e2e8f0" },
};

const CounterOrdersList = ({
  t,
  sortedCounterOrders,
  fetchMoreCounterData,
  hasMoreCounter,
  handleOrderClick,
  getStatusText,
  DEVICE_TYPE,
  isBedside = false,
  isCurbside = false,
}) => {
  // Bedside + Curbside share the storage-first column layout (no Consult col).
  const usesStorageColumn = isBedside || isCurbside;
  const columnCount = usesStorageColumn ? 7 : 8;

  const formatVehicleSubtitle = (order) => {
    const color = order && order.vehicle_color ? String(order.vehicle_color).trim() : "";
    const make = order && order.vehicle_make ? String(order.vehicle_make).trim() : "";
    const model = order && order.vehicle_model ? String(order.vehicle_model).trim() : "";
    return [color, make, model].filter(Boolean).join(" ");
  };

  const getLocationColumnHeader = () => {
    if (isCurbside) {
      return t("Console.Curbside.ParkingSpot", { defaultValue: "PARKING SPOT" });
    }
    if (isBedside) {
      return t("Assign.RoomNumber", { defaultValue: "ROOM" });
    }
    return t("Assign.Location") || "LOCATION";
  };

  const getLocationColumnValue = (order) => {
    if (isCurbside) {
      return order && order.parking_spot_label ? String(order.parking_spot_label).trim() : "";
    }
    if (isBedside) {
      return order && order.room_number ? order.room_number : "";
    }
    return order && order.storage_location ? order.storage_location : "";
  };
  // Derive is_consult_required: use order-level flag if present, else any item with is_consultrequire
  const isConsultRequired = (order) => {
    if (order == null) return false;
    if (order.is_consult_required === true || order.is_consult_required === "true") return true;
    const items = order.items || [];
    return Array.isArray(items) && items.some((item) => item && item.is_consultrequire === true);
  };

  const [epicFlagsDialogOpen, setEpicFlagsDialogOpen] = useState(false);
  const [selectedEpicFlagsOrder, setSelectedEpicFlagsOrder] = useState(null);
  const [itemsWithFlagsForDialog, setItemsWithFlagsForDialog] = useState([]);
  const [loadingFlagsDialog, setLoadingFlagsDialog] = useState(false);

  const closeEpicFlagsDialog = () => {
    setEpicFlagsDialogOpen(false);
    setSelectedEpicFlagsOrder(null);
    setItemsWithFlagsForDialog([]);
  };

  const getOrderItems = (orderOrDetails) => {
    if (!orderOrDetails) return [];
    return (
      orderOrDetails.items ||
      orderOrDetails.Items ||
      orderOrDetails.prescriptionItems ||
      orderOrDetails.prescription_items ||
      []
    );
  };

  const hasItemEpicFlags = (it) => {
    if (!it || !it.epic_flag_response) return false;
    const er = it.epic_flag_response;
    const hasFlags = er.flags && Array.isArray(er.flags) && er.flags.length > 0;
    const hasPrescriptionFlags =
      er.prescription_flags && Array.isArray(er.prescription_flags) && er.prescription_flags.length > 0;
    const hasCannotDispense =
      er.cannot_dispense_reasons &&
      Array.isArray(er.cannot_dispense_reasons) &&
      er.cannot_dispense_reasons.length > 0;
    return hasFlags || hasPrescriptionFlags || hasCannotDispense;
  };

  const fetchOrderDetailsById = async (orderId) => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    const token =
      user &&
      user.signInUserSession &&
      user.signInUserSession.idToken &&
      user.signInUserSession.idToken.jwtToken;
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(
      `${process.env.REACT_APP_BASEURL}deliveryconsole/getdetailsByID/batch`,
      { method: "POST", withCredentials: true, headers, body: JSON.stringify({ orderIds: [orderId] }) }
    );
    const data = await response.json().catch(() => ({}));
    const orders = (data && data.orders) || [];
    return orders && orders.length > 0 ? orders[0] : null;
  };

  // Socket returns epic_flag_response on each order; use for table icon. On click we fetch for multi-RX.
  const hasOrderEpicFlags = (order) => {
    if (!order || !order.epic_flag_response) return false;
    const er = order.epic_flag_response;
    const hasFlags = er.flags && Array.isArray(er.flags) && er.flags.length > 0;
    const hasPrescriptionFlags =
      er.prescription_flags && Array.isArray(er.prescription_flags) && er.prescription_flags.length > 0;
    const hasCannotDispense =
      er.cannot_dispense_reasons &&
      Array.isArray(er.cannot_dispense_reasons) &&
      er.cannot_dispense_reasons.length > 0;
    return hasFlags || hasPrescriptionFlags || hasCannotDispense;
  };

  const orderHasFlagsMap = useMemo(() => {
    const map = {};
    (sortedCounterOrders || []).forEach((order) => {
      if (!order || !order.id) return;
      map[order.id] = hasOrderEpicFlags(order);
    });
    return map;
  }, [sortedCounterOrders]);

  const handleOpenEpicFlagsDialogForOrder = async (order, event) => {
    if (event && typeof event.stopPropagation === "function") event.stopPropagation();
    if (!order || order.id == null) return;
    setSelectedEpicFlagsOrder(order);
    setEpicFlagsDialogOpen(true);
    setLoadingFlagsDialog(true);
    setItemsWithFlagsForDialog([]);
    let details = null;
    try {
      details = await fetchOrderDetailsById(order.id);
    } catch (e) {
      details = null;
    }
    const effective = details || order;
    const items = getOrderItems(effective);
    const withFlags = Array.isArray(items) ? items.filter((it) => hasItemEpicFlags(it)) : [];
    if (withFlags.length > 0) {
      setItemsWithFlagsForDialog(withFlags);
    } else if (hasOrderEpicFlags(order)) {
      setItemsWithFlagsForDialog([order]);
    } else {
      setItemsWithFlagsForDialog([]);
    }
    setLoadingFlagsDialog(false);
  };

  const rowLegendOrder = ["ARRIVED_PAID", "ARRIVED", "PAID", "ON_HOLD", "OTHER"];
  const rowLegend = rowLegendOrder.map((key) => ({
    key,
    label:
      key === "ARRIVED_PAID"
        ? (t("Console.Counter.LegendArrivedPaid") || "Arrived + Paid")
        : key === "ARRIVED"
          ? (t("Console.Counter.LegendArrived") || "Arrived")
          : key === "PAID"
            ? (t("Console.Counter.LegendPaid") || "Paid")
            : key === "ON_HOLD"
              ? (t("Console.Counter.LegendOnHold") || "On Hold")
              : (t("Console.Counter.LegendOther") || "Other"),
    ...ROW_COLORS[key],
  }));

  return (
    <Box sx={{ mb: 3, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          mb: 1,
          px: 0.5,
        }}
      >
        {rowLegend.map((item) => (
          <Box
            key={item.key}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "0.7rem",
              color: "#64748b",
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 1,
                backgroundColor: item.bg,
                border: "1px solid",
                borderColor: item.color,
                flexShrink: 0,
              }}
            />
            <span>{item.label}</span>
          </Box>
        ))}
      </Box>
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow
            sx={{
              background: "#f1f5f9",
              "& .MuiTableCell-head": {
                color: "#1e293b",
                fontWeight: 700,
                fontSize: "0.68rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                py: 1.5,
                borderBottom: "2px solid #cbd5e1",
              },
            }}
          >
            <TableCell align="left" sx={{ width: "22%" }}>
              {t("Home.StartConsultPopup.Name") || "NAME"}
            </TableCell>
            <TableCell align="center" sx={{ width: "18%" }}>
              {usesStorageColumn
                ? (t("Assign.StorageLocation") || "STORAGE LOCATION")
                : (t("Assign.PUCode") || "P/U CODE")}
            </TableCell>
            <TableCell align="center" sx={{ width: "12%" }}>
              {t("Console.Counter.Pay") || "PAY"}
            </TableCell>
            <TableCell align="center" sx={{ width: "14%" }}>
              {t("Console.Counter.Signature") || "SIGNATURE"}
            </TableCell>
            <TableCell align="center" sx={{ width: "14%" }}>
              {getLocationColumnHeader()}
            </TableCell>
            <TableCell align="center" sx={{ width: "9%" }}>
              {t("Assign.Flags") || "FLAGS"}
            </TableCell>
            {!usesStorageColumn && (
              <TableCell align="center" sx={{ width: "13%" }}>
                {"Consult"}
              </TableCell>
            )}
            <TableCell align="center" sx={{ width: "14%" }}>
              {t("Assign.Search.Status") || "STATUS"}
            </TableCell>
          </TableRow>
        </TableHead>
      </Table>
      <Box
        id="counterOrdersScrollableDiv"
        sx={{
          maxHeight: "250px",
          height: "auto",
          flex: "none",
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          position: "relative",
          "&::-webkit-scrollbar": {
            width: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f5f9",
            borderRadius: "5px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#7c3aed",
            borderRadius: "5px",
            border: "2px solid #f1f5f9",
            "&:hover": {
              background: "#6d28d9",
            },
          },
        }}
      >
        <InfiniteScroll
          dataLength={sortedCounterOrders.length}
          next={fetchMoreCounterData}
          hasMore={hasMoreCounter}
          scrollableTarget="counterOrdersScrollableDiv"
          scrollThreshold={0.8} // Load more when 80% scrolled (default is 1.0)
          loader={
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#7c3aed",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Loading more orders...
              </Typography>
            </Box>
          }
          endMessage={
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#94a3b8",
                  fontSize: "0.875rem",
                }}
              >
                {t("Console.YouHaveSeenIt") || "You have seen it all"}
              </Typography>
            </Box>
          }
        >
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableBody>
              {sortedCounterOrders && sortedCounterOrders.length > 0 ? (
                sortedCounterOrders.map((order, index) => {
                  const isHold = order.type_id === DEVICE_TYPE.Hold;
                  const isArrived = order.type_id === DEVICE_TYPE.Arrived;
                  const isPayrollDeduct = order.is_payroll_deduct === true;
                  const isPaid = order.is_paid === true || isPayrollDeduct;
                  const isPartiallyPaid =
                    !isPaid &&
                    ((order.amount && order.amount.isPartiallyPaid === true) ||
                      order.partial_payment === true ||
                      order.is_partially_paid === true);

                  const rowKey = isHold
                    ? "ON_HOLD"
                    : isArrived && isPaid
                      ? "ARRIVED_PAID"
                      : isArrived
                        ? "ARRIVED"
                        : isPaid
                          ? "PAID"
                          : "OTHER";
                  const rowColor = ROW_COLORS[rowKey];
                  const style = { backgroundColor: rowColor.bg, color: rowColor.color };

                  return (
                    <TableRow
                      key={order.id || index}
                      sx={{
                        ...style,
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: rowColor.hoverBg,
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        },
                        color: rowColor.color || "#334155",
                        "& td": {
                          borderColor: "#e2e8f0",
                          fontSize: "0.875rem",
                          verticalAlign: "top",
                        },
                      }}
                      onClick={() => handleOrderClick(order)}
                    >
                      <TableCell
                        align="left"
                        sx={{
                          width: "22%",
                          py: 1.5,
                          fontSize: "0.875rem",
                          lineHeight: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            fontWeight: 600,
                            color: "inherit",
                          }}
                        >
                          {order.customerName
                            ? order.customerName
                            : `${order.first_name || ""} ${order.last_name || ""}`.trim()}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: "18%",
                          py: 1.5,
                          fontSize: "0.875rem",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOrderClick(order);
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: "#7c3aed",
                            fontWeight: 600,
                            textDecoration: "none",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            display: "inline-block",
                            "&:hover": {
                              color: "#6d28d9",
                              textDecoration: "underline",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          {usesStorageColumn ? (order.storage_location || "-") : order.pucode}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: "12%",
                          py: 1.5,
                          fontSize: "0.875rem",
                          color:
                            Number(order.price) > 0
                              ? isPayrollDeduct
                                ? "#f59e0b"
                                : order.is_paid
                                  ? "#111827"
                                  : isPartiallyPaid
                                    ? "#f59e0b"
                                    : "#dc2626"
                              : "#111827",
                        }}
                      >
                        <Box
                          sx={{
                            fontWeight: 500,
                            color:
                              Number(order.price) > 0
                                ? isPayrollDeduct
                                  ? "#f59e0b"
                                  : order.is_paid
                                    ? "#111827"
                                    : isPartiallyPaid
                                      ? "#f59e0b"
                                      : "#dc2626"
                                : "#111827",
                          }}
                        >
                          {Number(order.price) > 0
                            ? isPayrollDeduct
                              ? "Pay Later"
                              : order.is_paid
                                ? "Paid"
                                : isPartiallyPaid
                                  ? "Partially Paid"
                                  : "Due"
                            : "None"}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: "14%",
                          py: 1.5,
                          fontSize: "0.875rem",
                        }}
                      >
                        <Box
                          sx={{
                            fontWeight: 500,
                            color: "inherit",
                          }}
                        >
                          {(order.signature_url &&
                          order.signature_url !== "" &&
                          order.signature_url !== "null" &&
                          order.signature_url !== "undefined") ||
                          (order.aob_signatures_complete === true)
                            ? "Done"
                            : ""}
                        </Box>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: "14%",
                          py: 1.5,
                          fontSize: "0.875rem",
                        }}
                      >
                        {(() => {
                          const locationValue = getLocationColumnValue(order);
                          const vehicleSubtitle = isCurbside ? formatVehicleSubtitle(order) : "";
                          if (!locationValue) {
                            return (
                              <Box
                                sx={{
                                  color: "#94a3b8",
                                  fontStyle: "italic",
                                }}
                              >
                                -
                              </Box>
                            );
                          }
                          return (
                            <Box sx={{ fontWeight: 500, color: "inherit" }}>
                              <Box component="span" sx={{ display: "block" }}>
                                {locationValue}
                              </Box>
                              {vehicleSubtitle ? (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "block",
                                    mt: 0.25,
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    color: "inherit",
                                    opacity: 0.75,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {vehicleSubtitle}
                                </Box>
                              ) : null}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: "9%",
                          py: 1.5,
                          fontSize: "0.875rem",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        {orderHasFlagsMap[order.id] ? (
                          <Tooltip title={t("Assign.ViewEpicFlags") || "View Epic Flags"}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenEpicFlagsDialogForOrder(order, e)}
                              sx={{
                                color: "#dc2626",
                                "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.1)" },
                                p: 0.5,
                              }}
                            >
                              <FlagIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Box sx={{ color: "#94a3b8", fontStyle: "italic" }}>-</Box>
                        )}
                      </TableCell>
                      {!usesStorageColumn && (
                        <TableCell
                          align="center"
                          sx={{
                            width: "13%",
                            py: 1.5,
                            fontSize: "0.875rem",
                          }}
                        >
                          <Box
                            sx={{
                              fontWeight: 500,
                              color: isConsultRequired(order) ? "#b45309" : "inherit",
                            }}
                          >
                            {isConsultRequired(order) ? "Required" : ""}
                          </Box>
                        </TableCell>
                      )}
                      <TableCell
                        align="center"
                        sx={{
                          width: "14%",
                          py: 1.5,
                          fontSize: "0.875rem",
                        }}
                      >
                        <Box
                          sx={{
                            fontWeight: 500,
                            color: "inherit",
                          }}
                        >
                          {getStatusText(order.type_id, DEVICE_TYPE)}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    align="center"
                    sx={{
                      py: 3,
                      fontSize: "0.875rem",
                      color: "text.secondary",
                    }}
                  >
                    {t("Assign.NoDataFound") || "No data found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </Box>

      <EpicFlagsDialog
        open={epicFlagsDialogOpen}
        onClose={closeEpicFlagsDialog}
        itemsWithFlags={itemsWithFlagsForDialog}
        loadingFlags={loadingFlagsDialog}
        epicFlagResponse={
          itemsWithFlagsForDialog.length === 0 && selectedEpicFlagsOrder
            ? selectedEpicFlagsOrder.epic_flag_response || null
            : null
        }
        item={
          itemsWithFlagsForDialog.length === 0 && selectedEpicFlagsOrder
            ? selectedEpicFlagsOrder
            : null
        }
        rxNumber={
          itemsWithFlagsForDialog.length === 0 && selectedEpicFlagsOrder
            ? (selectedEpicFlagsOrder.prescriptionNumber ||
                selectedEpicFlagsOrder.rxNo ||
                selectedEpicFlagsOrder.rx_no ||
                selectedEpicFlagsOrder.stock_code) ||
              null
            : null
        }
        t={t}
        accountId={
          selectedEpicFlagsOrder &&
          (selectedEpicFlagsOrder.parent_account_id != null
            ? selectedEpicFlagsOrder.parent_account_id
            : (selectedEpicFlagsOrder.account && selectedEpicFlagsOrder.account.id) != null
              ? selectedEpicFlagsOrder.account.id
              : selectedEpicFlagsOrder.customer_id)
        }
        epicFlagsOrderId={selectedEpicFlagsOrder && selectedEpicFlagsOrder.id}
      />
    </Box>
  );
};

CounterOrdersList.propTypes = {
  t: PropTypes.func.isRequired,
  sortedCounterOrders: PropTypes.array.isRequired,
  fetchMoreCounterData: PropTypes.func.isRequired,
  hasMoreCounter: PropTypes.bool.isRequired,
  handleOrderClick: PropTypes.func.isRequired,
  getStatusText: PropTypes.func.isRequired,
  DEVICE_TYPE: PropTypes.object.isRequired,
  isBedside: PropTypes.bool,
  isCurbside: PropTypes.bool,
};

CounterOrdersList.defaultProps = {
  sortedCounterOrders: [],
  hasMoreCounter: true,
  isBedside: false,
  isCurbside: false,
};

export default CounterOrdersList;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/CounterOrdersList.jsx